import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  Ship,
  CalendarDays,
  Calendar as CalendarIcon,
  MessageSquare,
  LogOut,
  Loader2,
  Save,
  RefreshCw,
  Trash2,
  Plus,
  ExternalLink,
  Copy,
  CheckCircle2,
  Settings as SettingsIcon,
  KeyRound,
  CreditCard,
  AlertTriangle,
  Pencil,
  X,
  Mail,
  Send,
  Images,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "../components/admin/ImagePicker";
import {
  adminMe,
  adminStats,
  adminListBookings,
  adminPatchBooking,
  adminApproveBooking,
  adminRejectBooking,
  adminListInquiries,
  adminListFeeds,
  adminAddFeed,
  adminSyncFeed,
  adminDeleteFeed,
  adminUpdateContent,
  adminCreateCharter,
  adminUpdateCharter,
  adminDeleteCharter,
  adminChangeCredentials,
  adminGetPaymentSettings,
  adminUpdatePaymentSettings,
  adminGetEmailSettings,
  adminUpdateEmailSettings,
  adminSendTestEmail,
  adminListMedia,
  adminUploadMedia,
  adminDeleteMedia,
  mediaUrl,
  fetchAllContent,
  fetchCharters,
  clearToken,
  setToken,
  getToken,
  API,
} from "../lib/api";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "content", label: "Content", icon: FileText },
  { id: "charters", label: "Charters", icon: Ship },
  { id: "media", label: "Media", icon: Images },
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "calendars", label: "Calendars", icon: CalendarIcon },
  { id: "inquiries", label: "Inquiries", icon: MessageSquare },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

const StatusBadge = ({ value }) => {
  const colors = {
    paid: "bg-secondary/10 text-secondary",
    pending: "bg-amber-100 text-amber-800",
    awaiting_approval: "bg-amber-100 text-amber-800",
    awaiting_payment: "bg-amber-100 text-amber-800",
    approved: "bg-blue-100 text-blue-800",
    confirmed: "bg-secondary/10 text-secondary",
    cancelled: "bg-red-100 text-red-700",
    rejected: "bg-red-100 text-red-700",
    completed: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] font-medium ${colors[value] || "bg-muted text-muted-foreground"}`}>
      {value}
    </span>
  );
};

// ---------------- Overview ----------------
const eur = (v) => `€${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const eur2 = (v) => `€${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ label, value, hint, accent = "primary", testId }) => (
  <div
    className={`${accent === "primary" ? "bg-white border border-border" : "bg-primary text-white"} p-6`}
    data-testid={testId}
  >
    <div className={`text-xs uppercase tracking-[0.2em] ${accent === "primary" ? "text-muted-foreground" : "text-white/60"}`}>
      {label}
    </div>
    <div className={`font-serif text-4xl mt-3 ${accent === "primary" ? "text-primary" : ""}`}>{value}</div>
    {hint && (
      <div className={`text-xs mt-2 ${accent === "primary" ? "text-muted-foreground" : "text-white/70"}`}>{hint}</div>
    )}
  </div>
);

const Overview = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminStats()
      .then((r) => { setStats(r); setLoading(false); })
      .catch(() => { toast.error("Failed to load stats"); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 size={16} className="animate-spin" /> Loading dashboard…
      </div>
    );
  }

  if (!stats) return null;

  const monthly = stats.monthly || [];
  const maxRev = Math.max(1, ...monthly.map((m) => m.revenue || 0));

  return (
    <div className="space-y-6" data-testid="overview-dashboard">
      {/* Top row — the four money numbers that matter */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Sales realised"
          value={eur(stats.sales_realised_eur)}
          hint="Trips that have already happened"
          accent="dark"
          testId="stat-sales-realised"
        />
        <StatCard
          label="Deposits collected"
          value={eur(stats.deposits_collected_eur)}
          hint="Cash in via Stripe (all paid bookings)"
          testId="stat-deposits"
        />
        <StatCard
          label="Sales pending"
          value={eur(stats.sales_pending_eur)}
          hint="Paid but trip date is still in the future"
          testId="stat-sales-pending"
        />
        <StatCard
          label="Unread inquiries"
          value={stats.unread_inquiries}
          hint="Contact-form messages waiting"
          testId="stat-inquiries"
        />
      </div>

      {/* Own vs partner split */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-border p-6" data-testid="stat-own-vs-partner">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
            Realised sales — where the money came from
          </div>
          <div className="space-y-4">
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-primary">Own boat</span>
                <span className="font-serif text-xl text-primary">{eur2(stats.own_sales_realised_eur)}</span>
              </div>
              <div className="text-xs text-muted-foreground">Full charter price (deposit + on-day balance)</div>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-primary">Partner commission</span>
                <span className="font-serif text-xl text-primary">{eur2(stats.partner_commission_realised_eur)}</span>
              </div>
              <div className="text-xs text-muted-foreground">Deposit only — our commission on partner charters</div>
            </div>
            <div className="pt-2 border-t border-border flex items-baseline justify-between">
              <span className="text-sm font-medium text-primary">Total realised</span>
              <span className="font-serif text-2xl text-primary">{eur2(stats.sales_realised_eur)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border p-6" data-testid="stat-averages">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Booking values</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-muted-foreground">Median</div>
              <div className="font-serif text-3xl text-primary">{eur(stats.median_booking_value_eur)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Average</div>
              <div className="font-serif text-3xl text-primary">{eur(stats.average_booking_value_eur)}</div>
            </div>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-4 text-center border-t border-border pt-4">
            <div>
              <div className="text-xs text-muted-foreground">Upcoming</div>
              <div className="font-serif text-2xl text-primary">{stats.upcoming_bookings}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Completed</div>
              <div className="font-serif text-2xl text-primary">{stats.completed_bookings}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Cancelled</div>
              <div className="font-serif text-2xl text-primary">{stats.cancelled_bookings}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly revenue chart */}
      <div className="bg-white border border-border p-6" data-testid="stat-monthly-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Last 6 months</div>
            <h3 className="font-serif text-2xl text-primary mt-1">Revenue by month</h3>
          </div>
          <div className="text-xs text-muted-foreground">Bar = revenue · count = bookings</div>
        </div>
        <div className="grid grid-cols-6 gap-4 h-52 items-end">
          {monthly.map((m) => {
            const h = maxRev > 0 ? Math.max(4, Math.round((m.revenue / maxRev) * 100)) : 4;
            return (
              <div key={m.label} className="flex flex-col items-center gap-2">
                <div className="font-serif text-lg text-primary">{eur(m.revenue)}</div>
                <div className="w-full bg-secondary/15 relative" style={{ height: `${h}%` }}>
                  <div className="absolute inset-0 bg-secondary/70" />
                </div>
                <div className="text-xs text-muted-foreground">{m.label}</div>
                <div className="text-[10px] text-muted-foreground">{m.bookings} bookings</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top charters + counts summary */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-white border border-border p-6" data-testid="stat-top-charters">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Top charters (all-time)</div>
          {(stats.top_charters || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">No paid bookings yet.</div>
          ) : (
            <div className="divide-y divide-border">
              {stats.top_charters.map((c) => (
                <div key={c.charter_id} className="py-3 flex items-center justify-between">
                  <span className="text-primary font-medium">{c.charter_title}</span>
                  <div className="text-right">
                    <div className="font-serif text-lg text-primary">{eur(c.revenue)}</div>
                    <div className="text-xs text-muted-foreground">{c.bookings} bookings</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-border p-6" data-testid="stat-counts">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">Bookings totals</div>
          <div className="space-y-3">
            <div className="flex justify-between"><span className="text-sm text-primary">All bookings</span><span className="font-serif text-lg">{stats.total_bookings}</span></div>
            <div className="flex justify-between"><span className="text-sm text-primary">Paid</span><span className="font-serif text-lg">{stats.paid_bookings}</span></div>
            <div className="flex justify-between"><span className="text-sm text-primary">Pending / awaiting</span><span className="font-serif text-lg">{stats.pending_bookings}</span></div>
            <div className="flex justify-between"><span className="text-sm text-primary">Cancelled / rejected</span><span className="font-serif text-lg">{stats.cancelled_bookings}</span></div>
          </div>
        </div>
      </div>

      <div className="bg-primary text-white p-8" data-testid="quick-tips">
        <div className="overline text-sand mb-2">How the numbers add up</div>
        <h3 className="font-serif text-2xl mb-3">Sales realised vs deposits collected</h3>
        <p className="text-white/80 max-w-3xl text-sm leading-relaxed">
          <strong>Deposits collected</strong> is every euro received through Stripe — it counts the moment a guest pays.
          <strong> Sales realised</strong> only counts a booking once the trip date has passed: for your own charters that
          means the full charter price (deposit + on-day balance), while for partner charters only the deposit is counted
          — that&rsquo;s your commission.
        </p>
      </div>
    </div>
  );
};

// ---------------- Content ----------------
const ContentEditor = () => {
  const [content, setContent] = useState({});
  const [active, setActive] = useState("hero");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    const c = await fetchAllContent();
    setContent(c);
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (content[active]) setDraft(JSON.stringify(content[active], null, 2));
  }, [content, active]);

  const save = async () => {
    setError("");
    let parsed;
    try {
      parsed = JSON.parse(draft);
    } catch (e) {
      setError("Invalid JSON: " + e.message);
      return;
    }
    setSaving(true);
    try {
      await adminUpdateContent(active, parsed);
      toast.success(`${active} saved`);
      await load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Collect image_url / logo_url fields from the current draft so we can render
  // picker widgets instead of forcing the admin to hand-edit JSON strings.
  const imageFields = React.useMemo(() => {
    try {
      const obj = JSON.parse(draft);
      const fields = [];
      // top-level scalar image fields
      Object.entries(obj).forEach(([k, v]) => {
        if (
          typeof v === "string" &&
          (k === "image_url" || k === "logo_url" || k === "background_url")
        ) {
          fields.push({ path: [k], label: k.replace(/_/g, " "), value: v });
        }
      });
      // items[].image_url for arrays of objects
      if (Array.isArray(obj.items)) {
        obj.items.forEach((it, i) => {
          if (it && typeof it === "object" && typeof it.image_url === "string") {
            fields.push({
              path: ["items", i, "image_url"],
              label: `Item ${i + 1} · ${it.name || it.title || "image"}`,
              value: it.image_url,
            });
          }
        });
      }
      return fields;
    } catch {
      return [];
    }
  }, [draft]);

  const setImageAtPath = (path, url) => {
    try {
      const obj = JSON.parse(draft);
      let ref = obj;
      for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
      ref[path[path.length - 1]] = url;
      setDraft(JSON.stringify(obj, null, 2));
    } catch (e) {
      // draft is not valid JSON — user will see the error on save
    }
  };

  const sections = Object.keys(content);
  const isSiteSection = active === "site";

  return (
    <div className="grid lg:grid-cols-12 gap-6">
      <div className="lg:col-span-3">
        <div className="bg-white border border-border p-2">
          {sections.map((s) => (
            <button
              key={s}
              onClick={() => setActive(s)}
              className={`w-full text-left px-4 py-3 text-sm capitalize transition-colors ${
                active === s ? "bg-primary text-white" : "hover:bg-muted/60"
              }`}
              data-testid={`content-tab-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3 px-1">
          Change images with the picker above the JSON. All other fields live in the JSON below.
        </p>
      </div>
      <div className="lg:col-span-9 bg-white border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-primary capitalize">{active}</h3>
          <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="content-save">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save changes</>}
          </button>
        </div>

        {isSiteSection && (
          <div className="mb-6 p-4 border border-border bg-muted/40" data-testid="site-logo-picker">
            <ImagePicker
              label="Site logo (shown in the top nav next to the brand name)"
              value={(() => { try { return JSON.parse(draft).logo_url || ""; } catch (e) { return ""; } })()}
              onChange={(v) => {
                try {
                  const obj = JSON.parse(draft);
                  obj.logo_url = v;
                  setDraft(JSON.stringify(obj, null, 2));
                } catch (e) {
                  // ignore — invalid JSON in draft
                }
              }}
              testId="logo-picker"
            />
            <div className="text-xs text-muted-foreground mt-2">
              Tip: transparent PNG or SVG works best. Remember to click <strong>Save changes</strong>.
            </div>
          </div>
        )}

        {imageFields.length > 0 && (
          <div className="mb-6 p-4 border border-border bg-muted/40 grid gap-4 md:grid-cols-2" data-testid="content-image-pickers">
            {imageFields.map((f, i) => (
              <ImagePicker
                key={`${active}-${f.path.join(".")}-${i}`}
                label={f.label}
                value={f.value}
                onChange={(v) => setImageAtPath(f.path, v)}
              />
            ))}
          </div>
        )}

        {error && <div className="text-sm text-destructive mb-3">{error}</div>}
        <textarea
          className="input-field font-mono text-xs min-h-[480px] resize-y"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="content-json"
          spellCheck={false}
        />
      </div>
    </div>
  );
};

// ---------------- Charters ----------------
const emptyCharter = () => ({
  slug: "",
  title: "New Charter",
  subtitle: "",
  duration_hours: 4,
  start_time: "09:00",
  end_time: "13:00",
  price_eur: 500,
  deposit_eur: 150,
  max_guests: 8,
  inclusions: ["Skipper & fuel", "Snorkelling gear"],
  image_url: "",
  order: 99,
  active: true,
  type: "own",
  partner_name: "",
});

const ChartersEditor = () => {
  const [list, setList] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    const r = await fetchCharters();
    setList(r);
  };
  useEffect(() => {
    load();
  }, []);

  const updateField = (id, key, value) => {
    setList((prev) => prev.map((c) => (c.id === id ? { ...c, [key]: value } : c)));
  };

  const save = async (c) => {
    setSavingId(c.id);
    try {
      await adminUpdateCharter(c.id, {
        title: c.title,
        subtitle: c.subtitle,
        duration_hours: Number(c.duration_hours),
        start_time: c.start_time,
        end_time: c.end_time,
        price_eur: Number(c.price_eur),
        deposit_eur: Number(c.deposit_eur),
        max_guests: Number(c.max_guests),
        inclusions: typeof c.inclusions === "string" ? c.inclusions.split("\n").filter(Boolean) : c.inclusions,
        image_url: c.image_url,
        active: c.active,
        order: Number(c.order || 0),
        type: c.type || "own",
        partner_name: c.partner_name || "",
      });
      toast.success("Charter updated");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}"? This cannot be undone.`)) return;
    try {
      await adminDeleteCharter(c.id);
      toast.success("Charter deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const startNew = () => {
    setDraft(emptyCharter());
  };

  const createCharter = async () => {
    if (!draft.slug || !draft.title) {
      toast.error("Slug and title are required");
      return;
    }
    setCreating(true);
    try {
      await adminCreateCharter({
        ...draft,
        duration_hours: Number(draft.duration_hours),
        price_eur: Number(draft.price_eur),
        deposit_eur: Number(draft.deposit_eur),
        max_guests: Number(draft.max_guests),
        order: Number(draft.order),
        type: draft.type || "own",
        partner_name: draft.partner_name || "",
        inclusions: typeof draft.inclusions === "string"
          ? draft.inclusions.split("\n").filter(Boolean)
          : draft.inclusions,
      });
      toast.success("Charter created");
      setDraft(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Create failed";
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-primary">Charter packages</h2>
        {!draft && (
          <button onClick={startNew} className="btn-primary text-sm" data-testid="add-charter-btn">
            <Plus size={14} /> New charter
          </button>
        )}
      </div>

      {draft && (
        <div className="bg-white border-2 border-primary p-6" data-testid="new-charter-form">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-serif text-2xl text-primary">New charter</h3>
            <button onClick={() => setDraft(null)} className="text-xs text-muted-foreground hover:text-primary">
              Cancel
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Type</label>
              <select
                className="input-field"
                value={draft.type || "own"}
                onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                data-testid="new-charter-type"
              >
                <option value="own">Own boat (instant Stripe payment)</option>
                <option value="partner">Partner boat (needs your approval)</option>
              </select>
            </div>
            {draft.type === "partner" && (
              <div>
                <label className="text-xs text-muted-foreground">Partner / operator name</label>
                <input
                  className="input-field"
                  value={draft.partner_name || ""}
                  onChange={(e) => setDraft({ ...draft, partner_name: e.target.value })}
                  placeholder="e.g. Sunset Yachts Malta"
                />
              </div>
            )}
            <div>
              <label className="text-xs text-muted-foreground">Slug (url-friendly id)</label>
              <input
                className="input-field"
                placeholder="sunset-cruise"
                value={draft.slug}
                onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
                data-testid="new-charter-slug"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Title</label>
              <input className="input-field" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="new-charter-title" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">Subtitle</label>
              <input className="input-field" value={draft.subtitle} onChange={(e) => setDraft({ ...draft, subtitle: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Start time</label>
              <input className="input-field" value={draft.start_time} onChange={(e) => setDraft({ ...draft, start_time: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">End time</label>
              <input className="input-field" value={draft.end_time} onChange={(e) => setDraft({ ...draft, end_time: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Duration (hours)</label>
              <input type="number" step="0.5" className="input-field" value={draft.duration_hours} onChange={(e) => setDraft({ ...draft, duration_hours: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Max guests</label>
              <input type="number" className="input-field" value={draft.max_guests} onChange={(e) => setDraft({ ...draft, max_guests: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Price (EUR)</label>
              <input type="number" className="input-field" value={draft.price_eur} onChange={(e) => setDraft({ ...draft, price_eur: e.target.value })} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Deposit (EUR)</label>
              <input type="number" className="input-field" value={draft.deposit_eur} onChange={(e) => setDraft({ ...draft, deposit_eur: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <ImagePicker
                label="Image"
                value={draft.image_url}
                onChange={(v) => setDraft({ ...draft, image_url: v })}
                testId="new-charter-image-picker"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">Inclusions (one per line)</label>
              <textarea
                className="input-field min-h-[100px] resize-y"
                value={Array.isArray(draft.inclusions) ? draft.inclusions.join("\n") : draft.inclusions}
                onChange={(e) => setDraft({ ...draft, inclusions: e.target.value })}
              />
            </div>
          </div>
          <div className="mt-5">
            <button onClick={createCharter} disabled={creating} className="btn-primary text-sm" data-testid="new-charter-save">
              {creating ? <><Loader2 size={14} className="animate-spin" /> Creating…</> : <><Plus size={14} /> Create charter</>}
            </button>
          </div>
        </div>
      )}

      {list.map((c) => (
        <div key={c.id} className="bg-white border border-border p-6" data-testid={`charter-edit-${c.slug}`}>
          <div className="flex items-center justify-between mb-5 gap-4">
            <h3 className="font-serif text-2xl text-primary">{c.title}</h3>
            <div className="flex items-center gap-4">
              <label className="text-xs flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!c.active}
                  onChange={(e) => updateField(c.id, "active", e.target.checked)}
                />
                Active
              </label>
              <button
                onClick={() => remove(c)}
                className="text-xs flex items-center gap-1 text-destructive hover:underline"
                data-testid={`delete-charter-${c.slug}`}
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground">Type</label>
              <select
                className="input-field"
                value={c.type || "own"}
                onChange={(e) => updateField(c.id, "type", e.target.value)}
                data-testid={`charter-type-${c.slug}`}
              >
                <option value="own">Own boat (instant Stripe payment)</option>
                <option value="partner">Partner boat (needs your approval)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">
                Partner / operator name {(c.type || "own") === "own" && <span className="text-muted-foreground/70">(unused for own boats)</span>}
              </label>
              <input
                className="input-field"
                value={c.partner_name || ""}
                onChange={(e) => updateField(c.id, "partner_name", e.target.value)}
                placeholder={c.type === "partner" ? "e.g. Sunset Yachts Malta" : ""}
                disabled={(c.type || "own") !== "partner"}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Title</label>
              <input className="input-field" value={c.title} onChange={(e) => updateField(c.id, "title", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Subtitle</label>
              <input className="input-field" value={c.subtitle} onChange={(e) => updateField(c.id, "subtitle", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Start time</label>
              <input className="input-field" value={c.start_time} onChange={(e) => updateField(c.id, "start_time", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">End time</label>
              <input className="input-field" value={c.end_time} onChange={(e) => updateField(c.id, "end_time", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Duration (hours)</label>
              <input type="number" step="0.5" className="input-field" value={c.duration_hours} onChange={(e) => updateField(c.id, "duration_hours", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Max guests</label>
              <input type="number" className="input-field" value={c.max_guests} onChange={(e) => updateField(c.id, "max_guests", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Price (EUR)</label>
              <input type="number" className="input-field" value={c.price_eur} onChange={(e) => updateField(c.id, "price_eur", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Deposit (EUR)</label>
              <input type="number" className="input-field" value={c.deposit_eur} onChange={(e) => updateField(c.id, "deposit_eur", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <ImagePicker
                label="Image"
                value={c.image_url}
                onChange={(v) => updateField(c.id, "image_url", v)}
                testId={`charter-image-picker-${c.slug}`}
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground">Inclusions (one per line)</label>
              <textarea
                className="input-field min-h-[100px] resize-y"
                value={Array.isArray(c.inclusions) ? c.inclusions.join("\n") : c.inclusions}
                onChange={(e) => updateField(c.id, "inclusions", e.target.value)}
              />
            </div>
          </div>
          <div className="mt-5">
            <button onClick={() => save(c)} disabled={savingId === c.id} className="btn-primary text-sm" data-testid={`save-charter-${c.slug}`}>
              {savingId === c.id ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save</>}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------- Emails panel ----------------
const EmailsPanel = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [newPassword, setNewPassword] = useState("");

  const load = async () => {
    try {
      const r = await adminGetEmailSettings();
      setS(r);
    } catch (e) {
      // ignore
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (!s) {
    return (
      <div className="bg-white border border-border p-6">
        <Loader2 className="animate-spin text-muted-foreground" size={16} />
      </div>
    );
  }

  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        smtp_host: s.smtp_host,
        smtp_port: Number(s.smtp_port) || 587,
        smtp_user: s.smtp_user || null,
        from_name: s.from_name,
        reply_to: s.reply_to || null,
        enabled: !!s.enabled,
        confirmation_subject: s.confirmation_subject,
        confirmation_body: s.confirmation_body,
        notify_inquiries: !!s.notify_inquiries,
        notify_to: s.notify_to || null,
        inquiry_subject: s.inquiry_subject,
        inquiry_body: s.inquiry_body,
        auto_receipts: !!s.auto_receipts,
        receipt_subject: s.receipt_subject,
        receipt_body: s.receipt_body,
        partner_request_admin_subject: s.partner_request_admin_subject,
        partner_request_admin_body: s.partner_request_admin_body,
        approval_subject: s.approval_subject,
        approval_body: s.approval_body,
        rejection_subject: s.rejection_subject,
        rejection_body: s.rejection_body,
      };
      if (newPassword) payload.smtp_password = newPassword;
      await adminUpdateEmailSettings(payload);
      toast.success("Email settings saved");
      setNewPassword("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a test recipient email");
    setTesting(true);
    try {
      await adminSendTestEmail({ to: testTo });
      toast.success(`Test email sent to ${testTo}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  const configured = s.smtp_user && s.password_set;

  return (
    <div className="bg-white border border-border p-6" data-testid="emails-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Mail size={18} className="text-secondary" />
          <h3 className="font-serif text-2xl text-primary">Booking confirmation emails</h3>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!s.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
            data-testid="emails-enabled"
          />
          <span className={s.enabled ? "text-emerald-700" : "text-muted-foreground"}>
            {s.enabled ? "Enabled" : "Disabled"}
          </span>
        </label>
      </div>

      {!configured && (
        <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 mb-6 text-sm text-amber-900" data-testid="emails-setup-notice">
          <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <div>
            <strong>Not connected yet.</strong> For Gmail, turn on 2-step verification then create an{" "}
            <a
              href="https://myaccount.google.com/apppasswords"
              target="_blank"
              rel="noopener noreferrer"
              className="underline inline-flex items-center gap-1"
            >
              App Password <ExternalLink size={11} />
            </a>{" "}
            (16 characters, no spaces). Paste that below as the SMTP password. Gmail defaults are pre-filled — you only
            need the email + app password.
          </div>
        </div>
      )}

      <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-xs text-muted-foreground">SMTP host</label>
          <input className="input-field" value={s.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">SMTP port</label>
          <input type="number" className="input-field" value={s.smtp_port || 587} onChange={(e) => set("smtp_port", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Gmail address (sender)</label>
          <input
            type="email"
            className="input-field"
            placeholder="hello@yourdomain.com"
            value={s.smtp_user || ""}
            onChange={(e) => set("smtp_user", e.target.value)}
            data-testid="emails-user"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">App password {s.password_set && <span className="text-emerald-700">(saved)</span>}</label>
          <input
            type="password"
            className="input-field font-mono"
            placeholder={s.password_set ? "•••••••• (change to update)" : "16-char app password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="emails-password"
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">From name</label>
          <input className="input-field" value={s.from_name || ""} onChange={(e) => set("from_name", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Reply-to (optional)</label>
          <input type="email" className="input-field" value={s.reply_to || ""} onChange={(e) => set("reply_to", e.target.value)} placeholder="Defaults to Gmail address" />
        </div>

        <div className="md:col-span-2 mt-2">
          <label className="text-xs text-muted-foreground">Confirmation subject</label>
          <input className="input-field" value={s.confirmation_subject || ""} onChange={(e) => set("confirmation_subject", e.target.value)} data-testid="emails-subject" />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-muted-foreground">Confirmation body</label>
          <textarea
            className="input-field min-h-[220px] resize-y font-mono text-xs"
            value={s.confirmation_body || ""}
            onChange={(e) => set("confirmation_body", e.target.value)}
            data-testid="emails-body"
            spellCheck={false}
          />
          <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
            Placeholders you can use inside the subject and body — they&apos;re replaced when the email is sent:
            <code className="ml-2 font-mono text-[11px]">{"{customer_name}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{charter_title}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{booking_date}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{start_time}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{end_time}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{guests}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{amount_due}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{payment_type}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{customer_email}"}</code>{" "}
            <code className="font-mono text-[11px]">{"{customer_phone}"}</code>
          </div>
        </div>

        <div className="md:col-span-2 flex flex-wrap gap-3 mt-2">
          <button type="submit" disabled={saving} className="btn-primary text-sm" data-testid="emails-save">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save email settings</>}
          </button>
        </div>
      </form>

      <div className="mt-10 pt-6 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-serif text-xl text-primary">Automatic deposit receipt</h4>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!s.auto_receipts}
              onChange={(e) => set("auto_receipts", e.target.checked)}
              data-testid="emails-auto-receipts"
            />
            <span className={s.auto_receipts ? "text-emerald-700" : "text-muted-foreground"}>
              {s.auto_receipts ? "Send on payment" : "Off"}
            </span>
          </label>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          The moment Stripe confirms a guest&rsquo;s deposit (or full payment), this email is sent to them automatically —
          no admin action needed. Use{" "}
          <code className="font-mono text-[11px]">{"{customer_name}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{charter_title}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{booking_date}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{start_time}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{end_time}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{guests}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{amount_due}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{total_eur}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{payment_type}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{booking_id}"}</code>.
        </p>
        <div className="grid gap-4">
          <div>
            <label className="text-xs text-muted-foreground">Receipt subject</label>
            <input
              className="input-field"
              value={s.receipt_subject || ""}
              onChange={(e) => set("receipt_subject", e.target.value)}
              data-testid="emails-receipt-subject"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Receipt body</label>
            <textarea
              className="input-field min-h-[200px] resize-y font-mono text-xs"
              value={s.receipt_body || ""}
              onChange={(e) => set("receipt_body", e.target.value)}
              data-testid="emails-receipt-body"
              spellCheck={false}
            />
          </div>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Remember to click <strong>Save email settings</strong> above after editing.
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-border">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-serif text-xl text-primary">Inquiry notifications</h4>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!s.notify_inquiries}
              onChange={(e) => set("notify_inquiries", e.target.checked)}
              data-testid="emails-notify-inquiries"
            />
            <span className={s.notify_inquiries ? "text-emerald-700" : "text-muted-foreground"}>
              {s.notify_inquiries ? "Forwarding to inbox" : "Off"}
            </span>
          </label>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Every message submitted through the website contact form is forwarded to your inbox in real time. Use{" "}
          <code className="font-mono text-[11px]">{"{name}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{email}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{phone}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{message}"}</code>{" "}
          as placeholders. The sender&rsquo;s email is set as Reply-To so you can respond directly from Gmail.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground">
              Forward inquiries to (defaults to the sender Gmail if blank)
            </label>
            <input
              type="email"
              className="input-field"
              placeholder="hello@islandsescape.com"
              value={s.notify_to || ""}
              onChange={(e) => set("notify_to", e.target.value)}
              data-testid="emails-notify-to"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground">Inquiry email subject</label>
            <input
              className="input-field"
              value={s.inquiry_subject || ""}
              onChange={(e) => set("inquiry_subject", e.target.value)}
              data-testid="emails-inquiry-subject"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground">Inquiry email body</label>
            <textarea
              className="input-field min-h-[160px] resize-y font-mono text-xs"
              value={s.inquiry_body || ""}
              onChange={(e) => set("inquiry_body", e.target.value)}
              data-testid="emails-inquiry-body"
              spellCheck={false}
            />
          </div>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Remember to click <strong>Save email settings</strong> above after editing these.
        </div>
      </div>

      <div className="mt-10 pt-6 border-t border-border">
        <h4 className="font-serif text-xl text-primary mb-2">Partner charter emails</h4>
        <p className="text-sm text-muted-foreground mb-4">
          Three templates used only for the partner-charter approval flow. Placeholders:{" "}
          <code className="font-mono text-[11px]">{"{customer_name}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{customer_email}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{customer_phone}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{charter_title}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{partner_name}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{booking_date}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{start_time}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{end_time}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{guests}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{amount_due}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{payment_type}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{booking_id}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{payment_url}"}</code>{" "}
          <code className="font-mono text-[11px]">{"{rejection_reason}"}</code>
        </p>

        <div className="space-y-6">
          <div className="border border-border p-4">
            <div className="text-sm font-medium text-primary mb-3">1 · Admin notification (new partner request)</div>
            <label className="text-xs text-muted-foreground">Subject</label>
            <input
              className="input-field"
              value={s.partner_request_admin_subject || ""}
              onChange={(e) => set("partner_request_admin_subject", e.target.value)}
              data-testid="emails-partner-admin-subject"
            />
            <label className="text-xs text-muted-foreground mt-3 block">Body</label>
            <textarea
              className="input-field min-h-[160px] resize-y font-mono text-xs"
              value={s.partner_request_admin_body || ""}
              onChange={(e) => set("partner_request_admin_body", e.target.value)}
              data-testid="emails-partner-admin-body"
              spellCheck={false}
            />
          </div>

          <div className="border border-border p-4">
            <div className="text-sm font-medium text-primary mb-3">2 · Guest — approval + payment link</div>
            <label className="text-xs text-muted-foreground">Subject</label>
            <input
              className="input-field"
              value={s.approval_subject || ""}
              onChange={(e) => set("approval_subject", e.target.value)}
              data-testid="emails-approval-subject"
            />
            <label className="text-xs text-muted-foreground mt-3 block">Body (must contain {"{payment_url}"})</label>
            <textarea
              className="input-field min-h-[200px] resize-y font-mono text-xs"
              value={s.approval_body || ""}
              onChange={(e) => set("approval_body", e.target.value)}
              data-testid="emails-approval-body"
              spellCheck={false}
            />
          </div>

          <div className="border border-border p-4">
            <div className="text-sm font-medium text-primary mb-3">3 · Guest — rejection</div>
            <label className="text-xs text-muted-foreground">Subject</label>
            <input
              className="input-field"
              value={s.rejection_subject || ""}
              onChange={(e) => set("rejection_subject", e.target.value)}
              data-testid="emails-rejection-subject"
            />
            <label className="text-xs text-muted-foreground mt-3 block">Body</label>
            <textarea
              className="input-field min-h-[160px] resize-y font-mono text-xs"
              value={s.rejection_body || ""}
              onChange={(e) => set("rejection_body", e.target.value)}
              data-testid="emails-rejection-body"
              spellCheck={false}
            />
          </div>
        </div>
        <div className="mt-3 text-xs text-muted-foreground">
          Remember to click <strong>Save email settings</strong> above after editing.
        </div>
      </div>

      <div className="mt-8 pt-6 border-t border-border">
        <div className="text-sm font-medium text-primary mb-2">Send a test email</div>
        <div className="flex flex-wrap gap-3">
          <input
            type="email"
            className="input-field flex-1 min-w-[240px]"
            placeholder="you@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            data-testid="emails-test-to"
          />
          <button
            onClick={sendTest}
            disabled={testing || !s.enabled || !configured}
            className="btn-outline text-sm"
            data-testid="emails-test-send"
          >
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send test
          </button>
        </div>
        {(!s.enabled || !configured) && (
          <div className="text-xs text-muted-foreground mt-2">
            Enable sending and save your Gmail + app password before sending a test.
          </div>
        )}
      </div>
    </div>
  );
};

// ---------------- Payments panel ----------------
const PaymentsPanel = () => {
  const [settings, setSettings] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    try {
      const r = await adminGetPaymentSettings();
      setSettings(r);
    } catch (e) {
      // ignore — keep last good state
    }
  };
  useEffect(() => {
    load();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!newKey || !(newKey.startsWith("sk_live_") || newKey.startsWith("sk_test_"))) {
      toast.error("Paste a Stripe secret key starting with sk_live_ or sk_test_");
      return;
    }
    setSaving(true);
    try {
      await adminUpdatePaymentSettings({ api_key: newKey });
      toast.success("Stripe key saved");
      setNewKey("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm("Stop using your Stripe account and fall back to the platform sandbox key?")) return;
    setClearing(true);
    try {
      await adminUpdatePaymentSettings({ api_key: "" });
      toast.success("Reverted to platform key");
      load();
    } catch {
      toast.error("Could not clear");
    } finally {
      setClearing(false);
    }
  };

  const isPlatform = settings?.platform_key_in_use;
  const isLive = settings?.mode === "live";

  return (
    <div className="bg-white border border-border p-6" data-testid="payments-panel">
      <div className="flex items-center gap-2 mb-4">
        <CreditCard size={18} className="text-secondary" />
        <h3 className="font-serif text-2xl text-primary">Payments (Stripe)</h3>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <div className="border border-border p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Mode</div>
          <div className={`font-serif text-2xl mt-1 ${isLive ? "text-emerald-700" : "text-amber-700"}`} data-testid="payments-mode">
            {isLive ? "LIVE" : "TEST"}
          </div>
        </div>
        <div className="border border-border p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Active key</div>
          <div className="font-mono text-sm mt-2 text-primary truncate" data-testid="payments-masked">
            {settings?.masked_key || "—"}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Source: {settings?.source === "database" ? "your Stripe account" : "Emergent platform (sandbox)"}
          </div>
        </div>
      </div>

      {isPlatform && (
        <div className="flex gap-3 p-4 bg-amber-50 border border-amber-200 mb-6" data-testid="payments-warning">
          <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <strong>No real payments are being collected.</strong> You&rsquo;re using the platform&rsquo;s sandbox key —
            checkouts go through Stripe&rsquo;s test mode and no money moves. Paste your own Stripe secret key below to
            send payouts to your account.
          </div>
        </div>
      )}

      <form onSubmit={save} className="space-y-3">
        <label className="text-xs text-muted-foreground">Stripe secret key</label>
        <input
          type="password"
          className="input-field font-mono text-sm"
          placeholder="sk_live_…  or  sk_test_…"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          data-testid="payments-new-key"
          autoComplete="off"
        />
        <div className="text-xs text-muted-foreground">
          Get this from your Stripe dashboard:{" "}
          <a
            href="https://dashboard.stripe.com/apikeys"
            target="_blank"
            rel="noopener noreferrer"
            className="text-secondary underline inline-flex items-center gap-1"
          >
            dashboard.stripe.com/apikeys <ExternalLink size={11} />
          </a>{" "}
          → Reveal <em>Secret key</em>. The key is stored only on your server, never returned to the browser, and never
          shown again after saving.
        </div>

        <div className="flex flex-wrap gap-3 pt-3">
          <button type="submit" disabled={saving} className="btn-primary text-sm" data-testid="payments-save">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save Stripe key</>}
          </button>
          {!isPlatform && (
            <button
              type="button"
              onClick={clear}
              disabled={clearing}
              className="btn-outline text-sm"
              data-testid="payments-clear"
            >
              {clearing ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remove and revert
            </button>
          )}
        </div>
      </form>

      <div className="mt-8 pt-6 border-t border-border text-sm text-muted-foreground space-y-2">
        <div className="font-medium text-primary">How charges work</div>
        <p>
          When a guest books, our backend creates a Stripe Checkout Session using the secret key set here. After
          successful payment, the booking is auto-confirmed and the funds appear in your Stripe balance (transferred to
          your bank on your Stripe payout schedule).
        </p>
        <p>
          If you save a <code className="font-mono text-xs">sk_test_</code> key the site stays in test mode (use Stripe
          test cards like <code className="font-mono text-xs">4242 4242 4242 4242</code>). Switch to a{" "}
          <code className="font-mono text-xs">sk_live_</code> key when ready to take real money.
        </p>
      </div>
    </div>
  );
};

// ---------------- Settings (Admin Credentials + Payments) ----------------
const SettingsTab = ({ onLoggedOut }) => {
  const [me, setMe] = useState(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminMe()
      .then((r) => {
        setMe(r);
        setNewEmail(r.email);
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error("Enter your current password");
      return;
    }
    const sameEmail = !newEmail || newEmail === me?.email;
    const noNewPassword = !newPassword;
    if (sameEmail && noNewPassword) {
      toast.error("Change either email or password");
      return;
    }
    if (newPassword && newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword && newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      const payload = { current_password: currentPassword };
      if (!sameEmail) payload.new_email = newEmail;
      if (newPassword) payload.new_password = newPassword;
      const r = await adminChangeCredentials(payload);
      if (r.token) setToken(r.token);
      toast.success("Credentials updated");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      adminMe()
        .then((m) => setMe(m))
        .catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-tab">
      <PaymentsPanel />
      <EmailsPanel />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={18} className="text-secondary" />
            <h3 className="font-serif text-2xl text-primary">Admin credentials</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            Change your admin email or password. You&rsquo;ll need to enter your current password to confirm.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground">Current password</label>
              <input
                type="password"
                className="input-field"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="settings-current-password"
              />
            </div>
            <div className="hairline" />
            <div>
              <label className="text-xs text-muted-foreground">Email address</label>
              <input
                type="email"
                className="input-field"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                data-testid="settings-new-email"
              />
              <div className="text-xs text-muted-foreground mt-1">Currently: {me?.email || "—"}</div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">New password</label>
              <input
                type="password"
                className="input-field"
                placeholder="Leave blank to keep current"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="settings-new-password"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Confirm new password</label>
              <input
                type="password"
                className="input-field"
                placeholder="Repeat new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="settings-confirm-password"
              />
            </div>
            <button type="submit" disabled={saving} className="btn-primary text-sm" data-testid="settings-save">
              {saving ? <><Loader2 size={14} className="animate-spin" /> Updating…</> : <><Save size={14} /> Update credentials</>}
            </button>
          </form>
        </div>

        <div className="bg-primary text-white p-6">
          <h3 className="font-serif text-2xl mb-2">Security tips</h3>
          <ul className="space-y-3 text-sm text-white/80 mt-5">
            <li className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-sand mt-0.5 shrink-0" />
              <span>Use a unique password — at least 12 characters, mixing letters, numbers and symbols.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-sand mt-0.5 shrink-0" />
              <span>If you change your email, log in next time with the new address.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-sand mt-0.5 shrink-0" />
              <span>Rotate your password every few months, especially after sharing access.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 size={16} className="text-sand mt-0.5 shrink-0" />
              <span>Sign out from public computers using the button in the top right.</span>
            </li>
          </ul>
          <button
            onClick={() => {
              clearToken();
              if (onLoggedOut) onLoggedOut();
            }}
            className="mt-8 text-xs px-4 py-2 border border-white/30 hover:bg-white/10 inline-flex items-center gap-2"
            data-testid="settings-signout"
          >
            <LogOut size={12} /> Sign out everywhere
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------- Bookings ----------------
const BookingRow = ({ b, charters, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [draft, setDraft] = useState(() => ({
    charter_id: b.charter_id,
    booking_date: b.booking_date,
    start_time: b.start_time,
    end_time: b.end_time,
    guests: b.guests,
    customer_name: b.customer_name,
    customer_email: b.customer_email,
    customer_phone: b.customer_phone,
    amount_due: b.amount_due,
    notes: b.notes || "",
    status: b.status,
  }));

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await adminPatchBooking(b.id, {
        ...draft,
        guests: Number(draft.guests),
        amount_due: Number(draft.amount_due),
      });
      toast.success("Booking updated");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await adminPatchBooking(b.id, { status });
      toast.success("Status updated");
      onChanged();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const confirmAndEmail = async () => {
    if (!window.confirm(`Confirm booking and send confirmation email to ${b.customer_email}?`)) return;
    setConfirming(true);
    try {
      const r = await adminPatchBooking(b.id, {
        status: "confirmed",
        send_confirmation_email: true,
      });
      if (r?.email?.ok) toast.success("Confirmed & email sent");
      else if (r?.email && !r.email.ok) toast.error(`Confirmed, but email failed: ${r.email.message}`);
      else toast.success("Confirmed");
      onChanged();
    } catch (e) {
      toast.error("Could not confirm");
    } finally {
      setConfirming(false);
    }
  };

  const approvePartner = async () => {
    if (!window.confirm(`Approve this partner booking and email a payment link to ${b.customer_email}?`)) return;
    setConfirming(true);
    try {
      const r = await adminApproveBooking(b.id);
      if (r?.email?.ok) toast.success("Approved — payment link emailed");
      else toast.error(`Approved, but email failed: ${r?.email?.message || "unknown"}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setConfirming(false);
    }
  };

  const rejectPartner = async () => {
    const reason = window.prompt(
      "Rejection reason (shown to the guest — keep it polite):",
      "The partner isn't available on the requested date."
    );
    if (reason === null) return;
    setConfirming(true);
    try {
      await adminRejectBooking(b.id, reason);
      toast.success("Rejected — guest notified");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setConfirming(false);
    }
  };

  const isPartner = b.charter_type === "partner";
  const isAwaiting = b.status === "awaiting_approval";

  if (!editing) {
    return (
      <div className="bg-white border border-border p-5 grid md:grid-cols-12 gap-4 items-center" data-testid={`booking-row-${b.id}`}>
        <div className="md:col-span-4">
          <div className="font-medium text-primary flex items-center gap-2">
            {b.charter_title}
            {isPartner && (
              <span className="text-[9px] tracking-[0.2em] uppercase px-2 py-0.5 bg-secondary/10 text-secondary">
                Partner{b.partner_name ? ` · ${b.partner_name}` : ""}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            {b.booking_date} · {b.start_time}–{b.end_time}
          </div>
          {b.confirmation_email_sent && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 mt-1 inline-flex items-center gap-1">
              <Mail size={10} /> Confirmation email sent
            </div>
          )}
          {b.confirmation_email_error && (
            <div className="text-[10px] text-destructive mt-1">Email err: {b.confirmation_email_error}</div>
          )}
          {b.receipt_email_sent && (
            <div className="text-[10px] uppercase tracking-[0.2em] text-secondary mt-1 inline-flex items-center gap-1">
              <Mail size={10} /> Receipt emailed
            </div>
          )}
          {b.receipt_email_error && (
            <div className="text-[10px] text-destructive mt-1">Receipt err: {b.receipt_email_error}</div>
          )}
        </div>
        <div className="md:col-span-3 text-sm">
          <div className="text-primary">{b.customer_name}</div>
          <div className="text-xs text-muted-foreground">{b.customer_email}</div>
          <div className="text-xs text-muted-foreground">{b.customer_phone}</div>
        </div>
        <div className="md:col-span-2 text-sm">
          <div className="text-primary">{b.guests} guests</div>
          <div className="text-xs text-muted-foreground">€{Number(b.amount_due).toLocaleString()} ({b.payment_type})</div>
        </div>
        <div className="md:col-span-1 flex flex-col gap-1">
          <StatusBadge value={b.status} />
          <StatusBadge value={b.payment_status} />
        </div>
        <div className="md:col-span-2 flex flex-col gap-2">
          {isPartner && isAwaiting && (
            <>
              <button
                onClick={approvePartner}
                disabled={confirming}
                className="text-xs px-3 py-2 bg-secondary text-white hover:bg-secondary/90 inline-flex items-center justify-center gap-1"
                data-testid={`approve-partner-${b.id}`}
              >
                {confirming ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                Approve &amp; send link
              </button>
              <button
                onClick={rejectPartner}
                disabled={confirming}
                className="text-xs px-3 py-2 border border-destructive/40 text-destructive hover:bg-destructive/5 inline-flex items-center justify-center gap-1"
                data-testid={`reject-partner-${b.id}`}
              >
                <X size={12} /> Reject
              </button>
            </>
          )}
          <button
            onClick={() => setEditing(true)}
            className="text-xs px-3 py-2 border border-border hover:bg-muted/40 inline-flex items-center justify-center gap-1"
            data-testid={`edit-booking-${b.id}`}
          >
            <Pencil size={12} /> Edit
          </button>
          {!isPartner && b.status !== "confirmed" && (
            <button
              onClick={confirmAndEmail}
              disabled={confirming}
              className="text-xs px-3 py-2 bg-primary text-white hover:bg-primary/90 inline-flex items-center justify-center gap-1"
              data-testid={`confirm-booking-${b.id}`}
            >
              {confirming ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Confirm &amp; email
            </button>
          )}
          <select
            className="input-field text-xs py-2"
            value={b.status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid={`booking-status-${b.id}`}
          >
            <option value="pending">pending</option>
            <option value="awaiting_approval">awaiting_approval</option>
            <option value="approved">approved</option>
            <option value="confirmed">confirmed</option>
            <option value="completed">completed</option>
            <option value="cancelled">cancelled</option>
            <option value="rejected">rejected</option>
          </select>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border-2 border-primary p-5" data-testid={`booking-edit-${b.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-medium text-primary">Editing booking</div>
        <button onClick={() => setEditing(false)} className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1">
          <X size={12} /> Cancel
        </button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Charter</label>
          <select
            className="input-field text-sm py-2"
            value={draft.charter_id}
            onChange={(e) => {
              const c = charters.find((x) => x.id === e.target.value);
              set("charter_id", e.target.value);
              if (c) {
                set("start_time", c.start_time);
                set("end_time", c.end_time);
              }
            }}
          >
            {charters.map((c) => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Date</label>
          <input type="date" className="input-field" value={draft.booking_date} onChange={(e) => set("booking_date", e.target.value)} data-testid={`edit-date-${b.id}`} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Guests</label>
          <input type="number" min={1} max={30} className="input-field" value={draft.guests} onChange={(e) => set("guests", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Start time</label>
          <input type="time" className="input-field" value={draft.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid={`edit-start-${b.id}`} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">End time</label>
          <input type="time" className="input-field" value={draft.end_time} onChange={(e) => set("end_time", e.target.value)} data-testid={`edit-end-${b.id}`} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Amount due (EUR)</label>
          <input type="number" step="0.01" className="input-field" value={draft.amount_due} onChange={(e) => set("amount_due", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Guest name</label>
          <input className="input-field" value={draft.customer_name} onChange={(e) => set("customer_name", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Email</label>
          <input type="email" className="input-field" value={draft.customer_email} onChange={(e) => set("customer_email", e.target.value)} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Phone</label>
          <input className="input-field" value={draft.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} />
        </div>
        <div className="md:col-span-3">
          <label className="text-xs text-muted-foreground">Internal notes</label>
          <textarea className="input-field min-h-[80px] resize-y" value={draft.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid={`save-booking-${b.id}`}>
          {saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <><Save size={14} /> Save changes</>}
        </button>
        <button onClick={() => setEditing(false)} className="btn-outline text-sm">Discard</button>
      </div>
    </div>
  );
};

const BookingsList = () => {
  const [items, setItems] = useState([]);
  const [charters, setCharters] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([adminListBookings(), fetchCharters()]);
      setItems(b);
      setCharters(c);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  if (items.length === 0)
    return <div className="bg-white border border-border p-8 text-center text-muted-foreground">No bookings yet.</div>;
  return (
    <div className="space-y-3">
      {items.map((b) => (
        <BookingRow key={b.id} b={b} charters={charters} onChanged={load} />
      ))}
    </div>
  );
};

// ---------------- Calendar feeds ----------------
const CalendarsTab = () => {
  const [feeds, setFeeds] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);

  const exportUrl = `${API}/ical/export.ics`;

  const load = async () => {
    const r = await adminListFeeds();
    setFeeds(r);
  };
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name || !url) return;
    setAdding(true);
    try {
      await adminAddFeed({ name, url });
      toast.success("Feed added & synced");
      setName(""); setUrl("");
      load();
    } catch (e) {
      toast.error("Failed to add feed");
    } finally {
      setAdding(false);
    }
  };
  const sync = async (id) => {
    const r = await adminSyncFeed(id);
    if (r.ok) toast.success(`Synced (${r.blocked_count} blocked dates)`);
    else toast.error("Sync failed: " + (r.error || ""));
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this feed?")) return;
    await adminDeleteFeed(id);
    load();
  };

  const copyExport = async () => {
    try {
      await navigator.clipboard.writeText(exportUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // clipboard unavailable
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-border p-6">
        <h3 className="font-serif text-2xl text-primary mb-2">Import iCal feeds</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Paste the iCal URL provided by Click&amp;Boat, GetMyBoat, BoatBooker, TripAdvisor, etc. Booked dates from those
          platforms will block availability on this site.
        </p>
        <form onSubmit={add} className="grid gap-3">
          <input
            className="input-field"
            placeholder="Source name (e.g. Click&Boat)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="feed-name"
          />
          <input
            className="input-field"
            placeholder="iCal URL (https://…)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="feed-url"
          />
          <button disabled={adding} className="btn-primary text-sm self-start" data-testid="feed-add">
            {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add &amp; sync
          </button>
        </form>

        <div className="mt-8 space-y-3">
          {feeds.length === 0 && <div className="text-sm text-muted-foreground">No feeds yet.</div>}
          {feeds.map((f) => (
            <div key={f.id} className="border border-border p-4 flex items-start justify-between gap-3" data-testid={`feed-row-${f.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-primary">{f.name}</div>
                <div className="text-xs text-muted-foreground truncate">{f.url}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {f.last_synced ? `Synced ${new Date(f.last_synced).toLocaleString()}` : "Never synced"} ·{" "}
                  {(f.blocked_dates || []).length} blocked dates
                </div>
                {f.last_error && <div className="text-xs text-destructive mt-1">{f.last_error}</div>}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <button onClick={() => sync(f.id)} className="text-xs px-3 py-1.5 border border-border hover:bg-muted/40" data-testid={`feed-sync-${f.id}`}>
                  <RefreshCw size={12} className="inline mr-1" /> Sync
                </button>
                <button onClick={() => remove(f.id)} className="text-xs px-3 py-1.5 border border-destructive/30 text-destructive hover:bg-destructive/5">
                  <Trash2 size={12} className="inline mr-1" /> Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-primary text-white p-6">
        <h3 className="font-serif text-2xl mb-2">Export your bookings</h3>
        <p className="text-sm text-white/80 mb-5">
          Add this feed URL to Click&amp;Boat, GetMyBoat, BoatBooker, TripAdvisor and your personal calendar to block out
          dates booked here.
        </p>
        <div className="bg-white/5 border border-white/15 p-3 text-xs font-mono break-all" data-testid="export-feed-url">
          {exportUrl}
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={copyExport} className="text-xs px-3 py-2 bg-white text-primary hover:bg-white/90" data-testid="copy-export-url">
            {copied ? <CheckCircle2 size={12} className="inline mr-1" /> : <Copy size={12} className="inline mr-1" />}
            {copied ? "Copied" : "Copy URL"}
          </button>
          <a href={exportUrl} target="_blank" rel="noopener noreferrer" className="text-xs px-3 py-2 border border-white/30 hover:bg-white/10">
            <ExternalLink size={12} className="inline mr-1" /> Preview .ics
          </a>
        </div>
        <div className="mt-8 text-xs text-white/70 leading-relaxed">
          <strong className="text-sand">How it works:</strong> Every confirmed booking on this site is automatically
          included in this iCalendar feed. Other platforms read it on a schedule (typically every 2-6 hours) and block
          your availability accordingly.
        </div>
      </div>
    </div>
  );
};

// ---------------- Media library ----------------
const MediaTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const inputRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminListMedia();
      setItems(r);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    let successes = 0;
    for (const f of files) {
      try {
        await adminUploadMedia(f, "");
        successes++;
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (successes) toast.success(`${successes} uploaded`);
    setUploading(false);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this image? Any pages using it will show a broken image.")) return;
    try {
      await adminDeleteMedia(id);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyUrl = async (url, id) => {
    const full = mediaUrl(url);
    try {
      await navigator.clipboard.writeText(full);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1200);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div className="space-y-6" data-testid="media-tab">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          uploadFiles(e.dataTransfer.files);
        }}
        className={`bg-white border-2 border-dashed p-10 text-center transition-colors ${
          dragOver ? "border-secondary bg-secondary/5" : "border-border"
        }`}
        data-testid="media-drop-zone"
      >
        <Upload size={28} className="text-muted-foreground mx-auto mb-3" />
        <h3 className="font-serif text-2xl text-primary mb-1">Upload images</h3>
        <p className="text-sm text-muted-foreground mb-5">
          Drop images here, or click to browse. JPEG, PNG, WebP, GIF or SVG · max 6 MB each.
        </p>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-primary text-sm"
          data-testid="media-upload-btn"
        >
          {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Upload size={14} /> Choose files</>}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml"
          className="hidden"
          onChange={(e) => uploadFiles(e.target.files)}
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-primary">Your images</h3>
          <div className="text-xs text-muted-foreground">{items.length} in library</div>
        </div>

        {loading && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Loading…</div>}

        {!loading && items.length === 0 && (
          <div className="bg-white border border-border p-8 text-center text-muted-foreground">
            No images yet — upload something to get started.
          </div>
        )}

        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} className="bg-white border border-border overflow-hidden group" data-testid={`media-item-${it.id}`}>
              <div className="aspect-[4/3] overflow-hidden bg-muted/40">
                <img src={mediaUrl(it.url)} alt={it.alt || it.filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <div className="text-xs font-medium text-primary truncate" title={it.filename}>{it.filename}</div>
                <div className="text-[11px] text-muted-foreground">
                  {(it.size_bytes / 1024).toFixed(0)} KB · {new Date(it.created_at).toLocaleDateString()}
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => copyUrl(it.url, it.id)}
                    className="flex-1 text-[11px] px-2 py-1.5 border border-border hover:bg-muted/40 inline-flex items-center justify-center gap-1"
                    data-testid={`media-copy-${it.id}`}
                  >
                    {copiedId === it.id ? <CheckCircle2 size={11} /> : <Copy size={11} />}
                    {copiedId === it.id ? "Copied" : "Copy URL"}
                  </button>
                  <button
                    onClick={() => remove(it.id)}
                    className="text-[11px] px-2 py-1.5 border border-destructive/30 text-destructive hover:bg-destructive/5"
                    data-testid={`media-delete-${it.id}`}
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ---------------- Inquiries ----------------
const InquiriesList = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    adminListInquiries().then(setItems).catch(() => {});
  }, []);
  if (items.length === 0)
    return <div className="bg-white border border-border p-8 text-center text-muted-foreground">No inquiries yet.</div>;
  return (
    <div className="space-y-3">
      {items.map((q) => (
        <div key={q.id} className="bg-white border border-border p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="font-medium text-primary">{q.name}</div>
            <div className="text-xs text-muted-foreground">{new Date(q.created_at).toLocaleString()}</div>
          </div>
          <div className="text-xs text-muted-foreground mb-3">{q.email} · {q.phone}</div>
          <div className="text-sm whitespace-pre-wrap">{q.message}</div>
        </div>
      ))}
    </div>
  );
};

// ---------------- Layout ----------------
const AdminDashboardPage = () => {
  const [tab, setTab] = useState("overview");
  const [authed, setAuthed] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    adminMe()
      .then(() => setAuthed(true))
      .catch(() => {
        clearToken();
        navigate("/admin/login");
      });
  }, [navigate]);

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-muted/40" data-testid="admin-dashboard">
      <header className="bg-primary text-white">
        <div className="container-x flex items-center justify-between py-5">
          <Link to="/" className="flex flex-col leading-none">
            <span className="font-serif text-xl">Islands Escape</span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-white/60 mt-1">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs text-white/70 hover:text-sand">View site →</Link>
            <button onClick={logout} className="text-xs flex items-center gap-1.5 hover:text-sand" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-8 grid lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="bg-white border border-border p-2 sticky top-6">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  tab === t.id ? "bg-primary text-white" : "hover:bg-muted/60"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon size={16} />
                {t.label}
              </button>
            ))}
          </nav>
        </aside>
        <main className="lg:col-span-9">
          {tab === "overview" && <Overview />}
          {tab === "content" && <ContentEditor />}
          {tab === "charters" && <ChartersEditor />}
          {tab === "media" && <MediaTab />}
          {tab === "bookings" && <BookingsList />}
          {tab === "calendars" && <CalendarsTab />}
          {tab === "inquiries" && <InquiriesList />}
          {tab === "settings" && <SettingsTab onLoggedOut={() => navigate("/admin/login")} />}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
